import { read, write } from './store.ts';

/** Light, dark, or whatever the device says. `system` is the default and asserts nothing. */
export type ThemeChoice = 'system' | 'light' | 'dark';

/**
 * Settings that outlive a session, and the switch that decides whether anything outlives it at all.
 *
 * `rememberProgress` is off until someone turns it on. That is what keeps the claim in the privacy
 * document true as written — by default, closing the tab leaves nothing behind — while still
 * allowing the two features that are impossible without memory: resuming a session, and drilling
 * the questions you keep getting wrong.
 */
export interface Preferences {
  /** Master switch for the attempt history and saved sessions. */
  readonly rememberProgress: boolean;
  /** Text scale as a percentage. 100 is the design size. */
  readonly textScale: number;
  readonly theme: ThemeChoice;
  /**
   * The chosen interface language, or null to follow the browser.
   *
   * Held as a plain string rather than the `Locale` union: this store is written by one version of
   * the application and read by the next, and a language that has since been removed must fall
   * back rather than typecheck. The i18n layer decides what is a language it can speak.
   */
  readonly locale: string | null;
}

export const DEFAULT_PREFERENCES: Preferences = {
  rememberProgress: false,
  textScale: 100,
  theme: 'system',
  locale: null,
};

export const TEXT_SCALES: readonly number[] = [90, 100, 110, 125, 150];
export const THEME_CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark'];

export function loadPreferences(): Preferences {
  return read('preferences', DEFAULT_PREFERENCES, revive);
}

export function savePreferences(preferences: Preferences): boolean {
  return write('preferences', preferences);
}

/**
 * Rebuilds preferences from whatever is on the device.
 *
 * A value written by a future version, or edited by hand, must not be able to produce an unreadable
 * interface — so an unknown text scale falls back to the design size rather than being trusted.
 */
function revive(value: unknown): Preferences | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const candidate = value as Partial<Record<keyof Preferences, unknown>>;
  const scale = candidate.textScale;
  const theme = candidate.theme;
  const locale = candidate.locale;

  return {
    rememberProgress: candidate.rememberProgress === true,
    textScale:
      typeof scale === 'number' && TEXT_SCALES.includes(scale) ? scale : DEFAULT_PREFERENCES.textScale,
    theme: THEME_CHOICES.includes(theme as ThemeChoice)
      ? (theme as ThemeChoice)
      : DEFAULT_PREFERENCES.theme,
    locale: typeof locale === 'string' && locale !== '' ? locale : null,
  };
}
